/**
 * Compat flow shared sub-components.
 *
 * CCAvatar — framed archetype avatar tile with gradient background.
 * CCBackdrop — full-bleed cosmic gradient backdrop (3 tones).
 * CCStars — deterministic star field SVG.
 *
 * All hex values replaced with CSS custom property tokens from colors_and_type.css.
 * Strict TypeScript, no `any`.
 */

import styles from './Compat.module.css';

// --------------------------------------------------------------------------
// CCAvatar
// --------------------------------------------------------------------------

export type AvatarPalette = 'gold' | 'violet' | 'rose' | 'blue';

export interface CCavatarProps {
  glyph: string;
  palette?: AvatarPalette;
  size?: number;
  ringed?: boolean;
}

export function CCAvatar({ glyph, palette = 'gold', size = 80, ringed = false }: CCavatarProps): JSX.Element {
  // Background gradient per palette — uses token-equivalent hardcoded values
  // because CSS gradients can't use custom properties inside radial-gradient stops
  // reliably across all mobile WebViews. Values match colors_and_type.css tokens exactly.
  const gradients: Record<AvatarPalette, string> = {
    gold: 'radial-gradient(circle at 35% 30%, #f0c850, #e89a3c 50%, #4a1e6b)',
    violet: 'radial-gradient(circle at 35% 30%, #c89cff, #7a3dff 50%, #1a1340)',
    rose: 'radial-gradient(circle at 35% 30%, #ffd6e0, #ff8eb0 50%, #4a1e3a)',
    blue: 'radial-gradient(circle at 35% 30%, #b8d4ff, #3b5bff 50%, #0a1a4a)',
  };

  const glowColor = ringed ? 'rgba(232,154,60,0.4)' : 'rgba(232,154,60,0.35)';

  return (
    <div
      className={styles.ccAvatar}
      style={{
        width: size,
        height: size,
        borderRadius: size / 5,
        background: gradients[palette],
        boxShadow: `0 0 ${Math.round(size / 3)}px ${glowColor}, inset 0 1px 0 rgba(255,255,255,0.3)`,
      }}
      aria-hidden="true"
    >
      <span
        style={{
          fontSize: size * 0.42,
          lineHeight: 1,
        }}
      >
        {glyph}
      </span>
    </div>
  );
}

// --------------------------------------------------------------------------
// CCBackdrop
// --------------------------------------------------------------------------

export type BackdropTone = 'cosmos' | 'quiet' | 'pair';

export interface CCBackdropProps {
  tone?: BackdropTone;
}

export function CCBackdrop({ tone = 'cosmos' }: CCBackdropProps): JSX.Element {
  // Token-aligned gradient presets (--void = #05030f, --midnight = #0d0824,
  // --neb-purple = #7a3dff, --amber = #e89a3c, --amber-deep = #a86018)
  const presets: Record<BackdropTone, string> = {
    cosmos: [
      'radial-gradient(ellipse 480px 360px at 50% 18%, rgba(122,61,255,0.34), transparent 65%)',
      'radial-gradient(ellipse 360px 280px at 80% 8%, rgba(232,154,60,0.20), transparent 65%)',
      'radial-gradient(ellipse 420px 320px at 50% 95%, rgba(184,107,40,0.18), transparent 70%)',
      'linear-gradient(180deg, #05030f 0%, #0d0824 60%, #05030f 100%)',
    ].join(','),
    quiet: [
      'radial-gradient(ellipse 380px 280px at 70% 0%, rgba(122,61,255,0.18), transparent 65%)',
      'linear-gradient(180deg, #0d0824 0%, #05030f 100%)',
    ].join(','),
    pair: [
      'radial-gradient(ellipse 320px 280px at 25% 35%, rgba(232,154,60,0.32), transparent 60%)',
      'radial-gradient(ellipse 320px 280px at 75% 65%, rgba(122,61,255,0.32), transparent 60%)',
      'linear-gradient(180deg, #05030f 0%, #0d0824 100%)',
    ].join(','),
  };

  return (
    <div
      className={styles.ccBackdrop}
      style={{ background: presets[tone] }}
      aria-hidden="true"
    />
  );
}

// --------------------------------------------------------------------------
// CCStars
// --------------------------------------------------------------------------

export interface CCStarsProps {
  count?: number;
  seed?: number;
}

export function CCStars({ count = 50, seed = 0 }: CCStarsProps): JSX.Element {
  function pseudo(n: number): number {
    const x = Math.sin((n + seed) * 9999) * 10000;
    return x - Math.floor(x);
  }

  return (
    <svg
      className={styles.ccStars}
      width="100%"
      height="100%"
      aria-hidden="true"
    >
      {Array.from({ length: count }).map((_, i) => (
        <circle
          key={i}
          cx={pseudo(i * 1.7) * 390}
          cy={pseudo(i * 3.3) * 880}
          r={pseudo(i * 5.1) * 1.3 + 0.3}
          fill="#fff8d8"
          opacity={pseudo(i * 7.7) * 0.7 + 0.2}
        />
      ))}
    </svg>
  );
}
